import React from "react";
import {
    Row, Col, Card, CardBody, CardHeader,
    FormGroup, Label, Input, Button,
    Spinner, Badge, Table,
} from "reactstrap";
import useFtoForm from "./useFtoForm";
import ApprovalPanel from "./ApprovalPanel";
import DayOptionRadios from "./DayOptionRadios";

const USA_OTHER = ["Jury Duty", "Bereavement", "Military Leave", "Other"];

// ─── Leave type pill ──────────────────────────────────────────────────────────
const LeaveTypePill = ({ label, active, disabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`btn btn-sm rounded-pill px-3 ${active ? "btn-primary" : "btn-outline-secondary"}`}
        style={{ fontSize: 13 }}
    >
        {label}
    </button>
);

// ─── Balance sidebar ──────────────────────────────────────────────────────────
const BalanceSidebar = ({ balances, netAvailable, balanceAfterRequest, ftoCount, empName }) => {
    const rows = [
        ["Current year FTOs", balances?.baseFtoEarned ?? "—"],
        ["FTO days used", balances?.ftoDaysUsed ?? "—"],
        ["Remaining shutdown days", balances?.remainingShutdownDays ?? "—"],
        ["Compensation days", balances?.compensationDays ?? "—"],
        ["Carried from previous year", balances?.carriedFromPrevious ?? "—"],
        ["Scheduled for future", balances?.scheduledForFuture ?? "—"],
        ["Days requested", typeof ftoCount === "number" ? ftoCount.toFixed(1) : "—"],
    ];
    const balNeg = typeof balanceAfterRequest === "number" && balanceAfterRequest < 0;

    return (
        <Card>
            <CardHeader className="py-2 px-3">
                <h6 className="mb-0" style={{ fontSize: 13, fontWeight: 600 }}>
                    {empName ? `${empName}'s FTO Breakdown` : "FTO Breakdown"}
                </h6>
            </CardHeader>
            <div>
                {rows.map(([lbl, val]) => (
                    <div key={lbl} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                        <span style={{ fontSize: 12, color: "#878a99" }}>{lbl}</span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{val}</span>
                    </div>
                ))}
            </div>
            <div className="px-3 py-2" style={{ background: "#eef2ff", borderTop: "1px solid #e9ebec" }}>
                <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#405189" }}>Net FTO available</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#405189" }}>
                        {typeof netAvailable === "number" ? netAvailable.toFixed(1) : "—"}
                    </span>
                </div>
            </div>
            <div className="px-3 py-2" style={{ background: balNeg ? "#f8d7da" : "#fff3cd" }}>
                <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 12, fontWeight: 600, color: balNeg ? "#842029" : "#856404" }}>
                        Balance after request
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: balNeg ? "#842029" : "#856404" }}>
                        {typeof balanceAfterRequest === "number" ? balanceAfterRequest.toFixed(1) : "—"}
                    </span>
                </div>
            </div>
        </Card>
    );
};

// ─── Main form ────────────────────────────────────────────────────────────────
const FtoForm = ({ ftoId = 0 }) => {
    const {
        form, setField,
        loading, saving,
        balances,
        netAvailable, balanceAfterRequest,
        ftoCount,
        conflicts,
        canEditEmployeeFields,
        canEditSupervisor,
        canEditHR,
        canEditMPC,
        isNew,
        auth,
        handleSubmit,
    } = useFtoForm({ ftoId });

    if (loading) return (
        <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
        </div>
    );

    const ro = !canEditEmployeeFields;

    const approvalStatus =
        form.hrApproval === true ? { label: "HR Approved", color: "success" } :
            form.hrApproval === false ? { label: "HR Denied", color: "danger" } :
                { label: "Pending", color: "warning" };

    return (
        <Row className="g-3">

            {/* ── Left: main form ─────────────────────────────────────────────────── */}
            <Col xl={8} lg={7}>
                <Card>
                    <CardHeader className="d-flex align-items-center gap-2 py-3">
                        <h5 className="card-title mb-0 flex-grow-1" style={{ fontSize: 15 }}>
                            {isNew ? "New FTO Request" : `FTO Request #${ftoId}`}
                        </h5>
                        <Badge color={approvalStatus.color} pill>{approvalStatus.label}</Badge>
                    </CardHeader>

                    <CardBody>

                        {/* Employee chip */}
                        <div className="d-flex align-items-center gap-2 rounded p-2 mb-3"
                            style={{ background: "#eef2ff" }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                                style={{ background: "#405189", width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                                {form.empId?.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                                <div className="fw-semibold" style={{ fontSize: 13, color: "#405189" }}>
                                    {auth.empId === form.empId ? "My Request" : form.empName ?? form.empId}
                                </div>
                                <div style={{ fontSize: 11, color: "#878a99" }}>EMP ID: {form.empId}</div>
                            </div>
                        </div>

                        {/* ── Date range ──────────────────────────────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>Date Range</p>

                        <Row className="g-2 mb-1 align-items-end">
                            <Col xs="auto">
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>Start date</Label>
                                    <Input
                                        type="date" bsSize="sm" style={{ width: 160 }}
                                        value={form.startDate} disabled={ro}
                                        min={(!auth.isSuperUser && !auth.isMPC && !auth.isManager)
                                            ? new Date().toISOString().split("T")[0] : undefined}
                                        onChange={(e) => setField("startDate", e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs="auto" className="pb-1">
                                <span className="text-muted" style={{ fontSize: 12 }}>to</span>
                            </Col>
                            <Col xs="auto">
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>
                                        End date <span className="text-muted fw-normal">(optional)</span>
                                    </Label>
                                    <div className="d-flex gap-1">
                                        <Input
                                            type="date" bsSize="sm" style={{ width: 160 }}
                                            value={form.endDate ?? ""} disabled={ro}
                                            onChange={(e) => setField("endDate", e.target.value)}
                                        />
                                        {form.endDate && !ro && (
                                            <Button size="sm" color="light" className="px-2"
                                                onClick={() => setField("endDate", "")}>✕</Button>
                                        )}
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row className="g-3 mb-3">
                            <Col md={6}>
                                <Label style={{ fontSize: 12 }}>Start day</Label>
                                <DayOptionRadios
                                    name="startDayOption" value={form.startDayOption}
                                    disabled={ro} onChange={(v) => setField("startDayOption", v)}
                                />
                            </Col>
                            <Col md={6} style={{ opacity: form.endDate ? 1 : 0.4 }}>
                                <Label style={{ fontSize: 12 }}>End day</Label>
                                <DayOptionRadios
                                    name="endDayOption" value={form.endDayOption}
                                    disabled={ro || !form.endDate}
                                    onChange={(v) => setField("endDayOption", v)}
                                />
                            </Col>
                        </Row>

                        {/* ── Type of leave ────────────────────────────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>Type of Leave</p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <LeaveTypePill label="Scheduled FTO"
                                active={form.typeOfLeave === "SCHEDULED"} disabled={ro}
                                onClick={() => setField("typeOfLeave", "SCHEDULED")} />
                            <LeaveTypePill label="Company Business"
                                active={form.typeOfLeave === "COMPANY"} disabled={ro}
                                onClick={() => setField("typeOfLeave", "COMPANY")} />
                            <LeaveTypePill label="Other"
                                active={form.typeOfLeave === "OTHER"} disabled={ro}
                                onClick={() => setField("typeOfLeave", "OTHER")} />
                            {form.typeOfLeave === "OTHER" && (
                                <Input type="select" bsSize="sm" style={{ width: 210 }}
                                    value={form.otherReason ?? ""} disabled={ro}
                                    onChange={(e) => setField("otherReason", e.target.value)}>
                                    <option value="">— select reason —</option>
                                    {USA_OTHER.map((r) => <option key={r} value={r}>{r}</option>)}
                                </Input>
                            )}
                        </div>

                        {/* ── HR Round 4 / Issue 4: scheduling + pay status ─────────────────
                            Independent of Type of Leave. "Unscheduled" = call-out (e.g. an
                            HR-entered same-day absence); "Unpaid" skips the automatic FTO
                            balance deduction but still counts as an absence. Defaults keep
                            historical behavior (Scheduled / Paid). */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <p className="text-uppercase fw-semibold mb-2"
                                    style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>Scheduling</p>
                                <div className="d-flex flex-wrap gap-2">
                                    <LeaveTypePill label="Scheduled"
                                        active={form.scheduledStatus === "Scheduled"} disabled={ro}
                                        onClick={() => setField("scheduledStatus", "Scheduled")} />
                                    <LeaveTypePill label="Unscheduled (call-out)"
                                        active={form.scheduledStatus === "Unscheduled"} disabled={ro}
                                        onClick={() => setField("scheduledStatus", "Unscheduled")} />
                                </div>
                            </Col>
                            <Col md={6}>
                                <p className="text-uppercase fw-semibold mb-2"
                                    style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>Pay</p>
                                <div className="d-flex flex-wrap gap-2">
                                    <LeaveTypePill label="Paid"
                                        active={form.payStatus === "Paid"} disabled={ro}
                                        onClick={() => setField("payStatus", "Paid")} />
                                    <LeaveTypePill label="Unpaid (no FTO deduction)"
                                        active={form.payStatus === "Unpaid"} disabled={ro}
                                        onClick={() => setField("payStatus", "Unpaid")} />
                                </div>
                            </Col>
                        </Row>

                        {/* ── Dept conflicts ───────────────────────────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>
                            Dept. conflicts on selected dates
                        </p>
                        <div className="mb-3" style={{ border: "1px solid #e9ebec", borderRadius: 6, overflow: "hidden" }}>
                            <Table size="sm" className="mb-0 table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ fontSize: 12 }}>Employee</th>
                                        <th style={{ fontSize: 12 }}>ID</th>
                                        <th style={{ fontSize: 12 }}>Start</th>
                                        <th style={{ fontSize: 12 }}>End</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conflicts.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-3"
                                                style={{ fontSize: 13, fontStyle: "italic" }}>
                                                No conflicts found
                                            </td>
                                        </tr>
                                    ) : conflicts.map((c, i) => (
                                        <tr key={i} style={{ fontSize: 13 }}>
                                            <td>{c.employee}</td>
                                            <td>{c.id}</td>
                                            <td>{c.start}</td>
                                            <td>{c.end ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* ── Employee comment ─────────────────────────────────────────────── */}
                        <FormGroup className="mb-3">
                            <Label style={{ fontSize: 12 }}>Employee's comments <span style={{ color: "#f06548" }}>*</span></Label>
                            <Input type="textarea" rows={3}
                                value={form.employeeComment ?? ""} readOnly={ro}
                                placeholder="Add a note about this request..."
                                onChange={(e) => setField("employeeComment", e.target.value)} />
                        </FormGroup>

                        {/* ── Approvals ────────────────────────────────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "#878a99", letterSpacing: ".5px" }}>Approvals</p>
                        <ApprovalPanel
                            form={form}
                            setField={setField}
                            canEditSupervisor={canEditSupervisor}
                            canEditHR={canEditHR}
                            canEditMPC={canEditMPC}
                            showMPC={false}
                            showExempt={auth.isSuperUser}
                        />

                        {/* Actions */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button color="light" tag="a" href="/attendance">Cancel</Button>
                            <Button color="primary" disabled={saving} onClick={handleSubmit}>
                                {saving && <Spinner size="sm" className="me-1" />}
                                {isNew ? "Submit Request" : "Save Changes"}
                            </Button>
                        </div>

                    </CardBody>
                </Card>
            </Col>

            {/* ── Right: balance sidebar ───────────────────────────────────────────── */}
            <Col xl={4} lg={5}>
                <BalanceSidebar
                    balances={balances}
                    netAvailable={netAvailable}
                    balanceAfterRequest={balanceAfterRequest}
                    ftoCount={ftoCount}
                    empName={form.empName}
                />
                <Card className="mt-3">
                    <CardHeader className="py-2 px-3">
                        <h6 className="mb-0" style={{ fontSize: 13, fontWeight: 600 }}>Quick tips</h6>
                    </CardHeader>
                    <CardBody className="py-2 px-3">
                        <ul className="mb-0 ps-3" style={{ fontSize: 12, color: "#878a99", lineHeight: 1.7 }}>
                            <li>End date is optional for single-day requests.</li>
                            <li>Balance turns red if it goes below zero.</li>
                            <li>MPC panel activates only for requests over 10 days.</li>
                        </ul>
                    </CardBody>
                </Card>
            </Col>

        </Row>
    );
};

export default FtoForm;